import React, {useCallback, useMemo} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {genericMessageHandler, formatPrice} from 'shared/helpers';
import {removeItem} from 'service/HardwareService';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {useAppDispatch} from 'store/customer';
import {
    invalidateJob,
    invalidateJobCost,
    invalidateTotalProductCount,
} from 'components/customer/Job/store/jobApi';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {useGetJobSundryId} from 'components/customer/Hardware/helper/useGetJobSundryId';
import {dispatchClearChanges} from 'store/customer/appSlice';

export const useSundries = () => {
    const {jobSundryId} = useGetJobSundryId();
    const {userProfile} = useAppContext();
    const {notify} = useNotificationContext();
    const {job, room, isPageDirty, actionButtonsEnabled, isJobProcessing} =
        useJobContext();
    const {showDialog, dialog} = useConfirmationDialog();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {jobId} = useParams();

    const [searchParams] = useSearchParams();

    const hardwareStoreUrl = `/v2/job/${job.displayId}/hardware`;

    const removeItemFromJob = (item, event) => {
        const button = event.target.parentElement;
        showDialog({
            title: 'Delete Item',
            message: 'Are you sure you want to delete this item from the job?',
            yes: async () => {
                try {
                    button.disabled = true;
                    await removeItem(item.id);

                    dispatchClearChanges();

                    button.disabled = false;
                    genericMessageHandler(
                        notify,
                        {message: 'Sundry item removed the from job'},
                        'success'
                    );

                    dispatch(invalidateJob());
                    dispatch(invalidateJobCost());
                    dispatch(invalidateTotalProductCount());

                    if (
                        searchParams.has('sundry') &&
                        searchParams.has('jobSundry') &&
                        searchParams.get('jobSundry') != ''
                    ) {
                        const sundryId = searchParams.get('sundry');

                        if (sundryId == item.sundryItemId) {
                            if (room && room.id) {
                                navigate(
                                    `/v2/job/${job.displayId}/room/${room.id}/dashboard`
                                );
                            } else {
                                navigate(`/v2/job/${job.displayId}/dashboard`);
                            }
                        }
                    }
                } catch (e) {
                    button.disabled = false;
                    genericMessageHandler(notify, e);
                }
            },
            buttons: [
                {
                    name: 'Cancel',
                    variant: 'warning',
                },
            ],
        });
    };

    const editItemFromJob = (row) => {
        navigate(
            `/v2/job/${jobId}/hardware?sundry=${row.sundryItemId}&category=${row.sundryCategoryId}&jobSundry=${row.id}`
        );
    };

    const strongFormatter = (value, row) => {
        if (row.id == Number(jobSundryId)) {
            return (
                <strong>
                    {row.quantity} x {value}
                </strong>
            );
        } else {
            return `${row.quantity} x ${value}`;
        }
    };

    const addHardware = useCallback(() => {
        navigate(hardwareStoreUrl);
    }, [hardwareStoreUrl]);

    const noVariationHardware = (variations) => {
        if (variations > 0) {
            return <>No Sundries.</>;
        } else {
            return (
                <>
                    No Sundries.{' '}
                    {job.displayId && isJobProcessing ? (
                        <span
                            style={{color: 'red', cursor: 'pointer'}}
                            onClick={addHardware}>
                            Click <strong>{userProfile.sundryMenuText} </strong>
                            to add one
                        </span>
                    ) : null}
                </>
            );
        }
    };

    const tableConfig = useMemo(() => {
        const tableConfig = {
            fields: [
                {
                    fieldName: 'sundryItemName',
                    title: 'Current ' + userProfile.sundryMenuText,
                    format: strongFormatter,
                },
                userProfile.show_pricing
                    ? {
                          fieldName: 'totalCalculatedPrice',
                          title: 'Cost',
                          width: 65,
                          format: function hardwareFormatPrice(value, row) {
                              return (
                                  <div
                                      style={
                                          job.status ==
                                          JOB_STATUS.JOB_PENDING_CONFIRMATION
                                              ? {textAlign: 'center'}
                                              : {}
                                      }>
                                      {jobSundryId == row.id ? (
                                          <strong>
                                              {' '}
                                              {formatPrice(value, row)}{' '}
                                          </strong>
                                      ) : (
                                          formatPrice(value, row)
                                      )}
                                  </div>
                              );
                          },
                      }
                    : {},
            ],
            data:
                (job.hasOwnProperty('sundryItems') &&
                    job.sundryItems.map((item) => item.attributes)) ||
                [],
            noRecordsText: <>{noVariationHardware(job.variationsConfirmed)}</>,
        };

        if (job.status != JOB_STATUS.JOB_PENDING_CONFIRMATION) {
            tableConfig.options = {
                title: '',
                width: 75,
                buttons: [
                    {
                        iconName: 'Options-Edit.svg',
                        title: 'Edit Product',
                        action: editItemFromJob,
                    },
                    {
                        iconName: 'Options-Delete.svg',
                        title: 'Delete Product',
                        action: removeItemFromJob,
                        confirmChanges: isPageDirty,
                    },
                ],
            };
        }

        if (!actionButtonsEnabled) {
            tableConfig.options = undefined;
        }

        return tableConfig;
    }, [job, userProfile, isPageDirty, actionButtonsEnabled, jobSundryId]);

    return {
        tableConfig,
        dialog,
    };
};
